import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Tag } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import SsoUsersRoles from 'libs/api/ssoUsersRoles';
import styles from './main/style.module.scss';
import SsoUsers from 'libs/api/ssoUsers';
import { useRecoilValue } from 'recoil';
import { getFilters } from '../../../../../hooks/useShops';
import uuid from 'react-uuid';

const api = new SsoUsersRoles('notifications');
const apiPermissionGroups = new SsoUsersRoles('permission_groups');
const usersApi = new SsoUsers();

const useNotificationGroups = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [premisionsGroups, setPremisionsGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const filtersData = useRecoilValue(getFilters);

  const {
    data,
    singleItem: singleShop,
    loading,
    onChangeTableFilter,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
    fetchList,
  } = useApi({
    resourceKey: 'notifications',
    singleResourceKey: 'notification',
    api,
    isMainPage: !id,
  });
  const handleCreateRole = useCallback(
    async (values, idFromTable) => {
      const user_ids = values?.user_ids;
      const emails = values?.emails?.join(',');
      setUpdateInProcess(true);
      try {
        const data = {
          title: values.title,
          emails,
          user_ids,
          shopping_center_id: values.shopping_center_id,
        };
        if (idFromTable) {
          if (data.user_ids) {
            await api.put(idFromTable, { user_ids: data.user_ids });
          } else {
            await api.put(idFromTable, { emails: data.emails.join(',') });
          }
        } else {
          if (!id || id === 'create') {
            data.notification_model_ids = [];
            await api.create({ data });
            navigate('/sso/notification/page/notification-groups');
            form?.resetFields();
          } else {
            await api.put(id, { data });
            navigate('/sso/notification/page/notification-groups');
          }
        }
        await fetchList();
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );
  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Nome'),
        dataIndex: 'title',
      },
      {
        title: t('Utenti'),
        dataIndex: 'users',
        render: (data, row) => {
          const emails = row?.emails || [];
          let avilableEmails = [];
          if (data.length < 3) {
            avilableEmails = emails.slice(0, 3 - data.length);
          }
          if (data.length <= 3) {
            return (
              <Row gutter={[8, 8]}>
                {data.map((i) => {
                  const onClose = (id) => {
                    const user_ids = data
                      .filter((j) => j.id !== id)
                      .map((j) => j.id);
                    handleCreateRole({ user_ids }, row.id);
                  };
                  return (
                    <Col key={i.id}>
                      <Tag
                        className={styles.tag}
                        closable={row.can_destroy}
                        onClose={() => onClose(i.id)}
                      >
                        {i.long_name}
                      </Tag>
                    </Col>
                  );
                })}
                {avilableEmails.map((i) => {
                  const onClose = (id) => {
                    handleCreateRole(
                      { emails: emails.filter((i) => i !== id) },
                      row.id,
                    );
                  };
                  return (
                    <Col key={uuid()}>
                      <Tag
                        className={styles.tag}
                        closable={row.can_destroy}
                        onClose={() => onClose(i)}
                      >
                        {i}
                      </Tag>
                    </Col>
                  );
                })}
                {emails.slice(avilableEmails.length).length > 0 && (
                  <Tag className={styles.tag}>
                    + {emails.slice(avilableEmails.length).length}
                  </Tag>
                )}
              </Row>
            );
          } else {
            return (
              <Row gutter={[8, 8]}>
                {data.slice(0, 3).map((i) => {
                  const onClose = (id) => {
                    const user_ids = data
                      .filter((j) => j.id !== id)
                      .map((j) => j.id);
                    handleCreateRole({ user_ids }, row.id);
                  };
                  return (
                    <Col key={i.id}>
                      <Tag
                        className={styles.tag}
                        closable={row.can_destroy}
                        onClose={() => onClose(i.id)}
                      >
                        {i.long_name}
                      </Tag>
                    </Col>
                  );
                })}
                <Tag className={styles.tag}>
                  + {data.slice(3).length + emails.length}
                </Tag>
              </Row>
            );
          }
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) => (
          <Row gutter={16} justify={'end'}>
            {row.can_update && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteFilled />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [t, pathname]);

  useEffect(() => {
    if (+id && singleShop) {
      try {
        let fieldsValues = [];
        let user_ids = [];
        let emails = [];
        for (let key in singleShop) {
          if (key === 'users') {
            user_ids = [
              ...user_ids,
              ...(singleShop[key]?.map((i) => i.id) || []),
            ];
          } else if (key === 'emails') {
            fieldsValues.push({
              name: 'emails',
              value: singleShop[key] || [],
            });
          } else if (key === 'shopping_center') {
            fieldsValues.push({
              name: 'shopping_center_id',
              value: singleShop[key].id,
            });
          } else if (key !== 'id' && key !== 'extrastock_map') {
            fieldsValues.push({
              name: key,
              value: singleShop[key],
            });
          }
        }
        fieldsValues.push({
          name: 'user_ids',
          value: user_ids,
        });
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    } else {
      form?.resetFields();
    }
  }, [id, singleShop, form]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { users = [] },
        } = await usersApi.list({
          params: {
            per: 1000,
            exclude_user_role: 4,
          },
        });
        setUsers(users);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  return {
    columns,
    data,
    singleShop,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    premisionsGroups,
    handleCreateRole,
    onDelete,
    users,
    shoppingCenters: filtersData?.shopping_centers || [],
  };
};

export default useNotificationGroups;
